import { z } from 'zod';

/**
 * Play limits of an arena (RN-B2, RN-B7). Stored per arena in `limit_policy`
 * and edited in the back-office.
 */
export const limitPolicySchema = z.object({
  maxContinuousMinutes: z
    .number()
    .int()
    .min(1)
    .max(24 * 60),
  breakMinutes: z
    .number()
    .int()
    .min(0)
    .max(24 * 60),
  maxDailyMinutes: z
    .number()
    .int()
    .min(1)
    .max(24 * 60),
  associateExtraMinutes: z
    .number()
    .int()
    .min(0)
    .max(24 * 60),
  meritExtraMinutes: z
    .number()
    .int()
    .min(0)
    .max(24 * 60),
  warnBeforeMinutes: z.number().int().min(0).max(120),
});
export type LimitPolicy = z.infer<typeof limitPolicySchema>;

/** Values agreed on 2026-10-16: 1h30 in a row, 30 min break, 3 h a day, +30 min extras. */
export const DEFAULT_LIMIT_POLICY: LimitPolicy = {
  maxContinuousMinutes: 90,
  breakMinutes: 30,
  maxDailyMinutes: 180,
  associateExtraMinutes: 30,
  meritExtraMinutes: 30,
  warnBeforeMinutes: 10,
};

export interface PlayInterval {
  start: Date;
  /** `null` while the session is still open. */
  end: Date | null;
}

export interface LimitExtras {
  isAssociate: boolean;
  hasMerit: boolean;
  /** Minutes a monitor granted today when nobody was waiting (RN-B3). */
  grantedMinutes: number;
}

export const NO_EXTRAS: LimitExtras = { isAssociate: false, hasMerit: false, grantedMinutes: 0 };

export const limitStateSchema = z.enum([
  /** Not playing and free to start. */
  'idle',
  /** Not playing, inside the mandatory break. Unlocking now is allowed but flagged (RN-B9). */
  'on_break',
  'ok',
  /** Playing, within `warnBeforeMinutes` of a limit. */
  'warning',
  'over_continuous',
  'over_daily',
]);
export type LimitState = z.infer<typeof limitStateSchema>;

export const limitStatusSchema = z.object({
  state: limitStateSchema,
  playedTodayMinutes: z.number(),
  dailyLimitMinutes: z.number(),
  continuousMinutes: z.number(),
  continuousLimitMinutes: z.number(),
  /** When the current run reaches the "in a row" limit; null when not playing. */
  continuousLimitAt: z.string().datetime().nullable(),
  /** When today's total reaches the daily limit if play goes on; null when not playing. */
  dailyLimitAt: z.string().datetime().nullable(),
  /** End of the mandatory break while `on_break`. */
  breakUntil: z.string().datetime().nullable(),
});
export type LimitStatus = z.infer<typeof limitStatusSchema>;

const MINUTE = 60_000;

function toMinutes(ms: number): number {
  return Math.round((ms / MINUTE) * 10) / 10;
}

/**
 * Where a youth stands against the limits at `now`.
 *
 * `intervals` are the youth's sessions that touch `[dayStart - break, now]`,
 * from every arena. The PC never locks on this result (RN-B6): it drives the
 * warnings on the PC and the colours in the monitor's room view.
 *
 * Rules (the first two are inferred and documented in the Arena README):
 * - A "run" is a chain of sessions separated by gaps shorter than the break.
 *   Someone who changes PC or logs out for five minutes is still in the same
 *   run, and the short gap counts towards "in a row".
 * - Today's total counts only the time inside the Lisbon day, and only real
 *   session time, never the gaps.
 * - Associate and merit extras raise the daily limit (RN-B7). A monitor grant
 *   raises both limits: it is how the monitor lets someone keep playing when
 *   nobody is waiting (RN-B3).
 */
export function computeLimitStatus(
  intervals: readonly PlayInterval[],
  policy: LimitPolicy,
  extras: LimitExtras,
  now: Date,
  dayStart: Date,
): LimitStatus {
  const nowMs = now.getTime();
  const breakMs = policy.breakMinutes * MINUTE;
  const open = intervals.some((i) => i.end === null);
  const sorted = intervals
    .map((i) => ({ start: i.start.getTime(), end: i.end === null ? nowMs : i.end.getTime() }))
    .filter((i) => i.start <= nowMs)
    .sort((a, b) => a.start - b.start);

  let playedMs = 0;
  for (const i of sorted) {
    const from = Math.max(i.start, dayStart.getTime());
    const to = Math.min(i.end, nowMs);
    if (to > from) playedMs += to - from;
  }

  // Latest run: walk back while the gaps stay shorter than the break.
  let runStart = 0;
  let runEnd = 0;
  const last = sorted[sorted.length - 1];
  if (last) {
    runStart = last.start;
    runEnd = last.end;
    for (let k = sorted.length - 2; k >= 0; k -= 1) {
      const i = sorted[k]!;
      runEnd = Math.max(runEnd, i.end);
      if (runStart - i.end < breakMs) runStart = Math.min(runStart, i.start);
      else break;
    }
  }

  const grantMs = extras.grantedMinutes * MINUTE;
  const continuousLimitMs = policy.maxContinuousMinutes * MINUTE + grantMs;
  const dailyLimitMs =
    (policy.maxDailyMinutes +
      (extras.isAssociate ? policy.associateExtraMinutes : 0) +
      (extras.hasMerit ? policy.meritExtraMinutes : 0)) *
      MINUTE +
    grantMs;
  const warnMs = policy.warnBeforeMinutes * MINUTE;

  const base = {
    playedTodayMinutes: toMinutes(playedMs),
    dailyLimitMinutes: toMinutes(dailyLimitMs),
    continuousLimitMinutes: toMinutes(continuousLimitMs),
  };

  if (!open) {
    const runMs = last ? runEnd - runStart : 0;
    const sinceEnd = nowMs - runEnd;
    if (last && runMs >= continuousLimitMs && sinceEnd < breakMs) {
      return {
        ...base,
        state: 'on_break',
        continuousMinutes: toMinutes(runMs),
        continuousLimitAt: null,
        dailyLimitAt: null,
        breakUntil: new Date(runEnd + breakMs).toISOString(),
      };
    }
    return {
      ...base,
      state: 'idle',
      continuousMinutes: last && sinceEnd < breakMs ? toMinutes(runMs) : 0,
      continuousLimitAt: null,
      dailyLimitAt: null,
      breakUntil: null,
    };
  }

  const continuousMs = nowMs - runStart;
  let state: LimitState = 'ok';
  if (playedMs >= dailyLimitMs) state = 'over_daily';
  else if (continuousMs >= continuousLimitMs) state = 'over_continuous';
  else if (playedMs >= dailyLimitMs - warnMs || continuousMs >= continuousLimitMs - warnMs) {
    state = 'warning';
  }

  return {
    ...base,
    state,
    continuousMinutes: toMinutes(continuousMs),
    continuousLimitAt: new Date(runStart + continuousLimitMs).toISOString(),
    dailyLimitAt: new Date(nowMs + Math.max(0, dailyLimitMs - playedMs)).toISOString(),
    breakUntil: null,
  };
}
